import type { Area } from 'core/visitRequestForm/types';
import type { DropdownOption } from 'components/Elements/Dropdown/types';

export interface AreaAccessValidationParams {
    selectedDates: DropdownOption[];
    selectedSiteAreas: DropdownOption[];
    applyToAll: boolean;
    selectedVisitors: number[];
    siteAreas?: Area[] | undefined;
}

export interface ValidationError {
    message: string;
    description: string;
}

/**
 * Real (non-synthetic) site area ids chosen by the user. The "All Areas"
 * option (value 'all') is a UI convenience and is not a persistable area id.
 */
export const getSelectedSiteAreaIds = (selectedSiteAreas: DropdownOption[]): number[] => selectedSiteAreas
    .filter((area) => area.value !== 'all')
    .map((area) => parseInt(area.value, 10));

/**
 * Validates a "Save Area Access" selection. Returns the error to show, or null
 * when the selection is valid.
 *
 * Crucially, this checks the real selected area ids rather than the raw
 * dropdown selection: on a site with no site areas the only option is the
 * synthetic "All Areas", which resolves to zero real ids. Saving that would
 * silently lose the visitor/shift assignment because the backend only persists
 * (visitor, date, area) rows, so we block it with a clear message.
 */
export const validateAreaAccessSelection = (params: AreaAccessValidationParams): ValidationError | null => {
    const {
        selectedDates, selectedSiteAreas, applyToAll, selectedVisitors, siteAreas,
    } = params;

    if (selectedDates.length === 0) {
        return {
            message: 'Date Not Selected',
            description: 'Please select at least one date to proceed',
        };
    }

    if (getSelectedSiteAreaIds(selectedSiteAreas).length === 0) {
        if (!siteAreas?.length) {
            return {
                message: 'No Site Areas Available',
                description: 'This site has no site areas set up, so site access cannot be saved. Please contact an administrator to add site areas before requesting access.',
            };
        }
        return {
            message: 'Site Area Not Selected',
            description: 'Please select at least one site area to proceed',
        };
    }

    if (!applyToAll && selectedVisitors.length === 0) {
        return {
            message: 'Visitors Not Selected',
            description: 'Please select at least one visitor to proceed',
        };
    }

    return null;
};
